import {
  Box,
  Button,
  Divider,
  Flex,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Grid,
  GridItem,
  HStack,
  IconButton,
  ListItem,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Select,
  Switch,
  Table,
  TableContainer,
  Tag,
  Tbody,
  Td,
  Text,
  Textarea,
  Th,
  Thead,
  Tr,
  UnorderedList,
  VStack,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import { ArrowDownIcon, ArrowUpIcon } from '@chakra-ui/icons';
import React, {
  Context,
  useContext,
  useEffect,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { FaPlusCircle } from "react-icons/fa";
import { FaCircleMinus } from "react-icons/fa6";
import FilterTextBox from '../../components/FilterTextBox';
import { WebAPIContext } from '../../contexts/WebAPIContext';
import { Messages } from '../../contexts/constants';
import {
  ChecklistRecord,
  Errors
} from '../../contexts/types';
import { formatLocalDateTime } from '../../utils/utils';

const CheckListMaster: React.FC = () => {
  const { t } = useTranslation();
  const toast = useToast();
  const WebAPI = useContext(WebAPIContext);

  const TITLE = t('features.MasterMaintenance.CheckListMaster.title');

  const headerCellStyle = {
    fontWeight: "bold",
    fontSize: "14px",
    whiteSpace: "normal" as const,
    wordBreak: "break-word" as const,
    backgroundColor: "gray.100",
  };

  const bodyCellStyle = {
    whiteSpace: 'normal' as const,
    wordBreak: 'break-word' as const,
    bgColor: "white",
  };

  const fixedCol1Style = {
    position: "sticky" as const,
    left: "0",
    zIndex: "1",
  };

  const fixedCol2Style = {
    position: "sticky" as const,
    left: "100px",
    zIndex: "1",
    boxShadow: "2px 0 4px rgba(0,0,0,0.1)",
  };

  const hoverStyle = { bg: "gray.200" };
  const labelStyle = { fontWeight: "bold", whiteSpace: "normal" as const, wordBreak: "break-word" as const, marginTop: "8px", width: 200 };

  const [masterData, setMasterData] = useState<ChecklistRecord[]>([]);

  const initialEditTarget: ChecklistRecord = {
    nodeId: "",
    parentNodeId: null,
    nodeClass: "PRINCIPLE",
    checklistNo: "1",
    contentNameJa: "",
    contentNameEn: "",
    conflictingConsiderationsJa: [],
    conflictingConsiderationsEn: [],
    deleted: false,
    order: 0,
    createDate: "",
    updateDate: "",
  };

  const [editTarget, setEditTarget] = useState<ChecklistRecord>(initialEditTarget);
  const [originalEditTarget, setOriginalEditTarget] = useState<ChecklistRecord>(initialEditTarget);
  const [originalChildItemsList, setOriginalChildItemsList] = useState<ChecklistRecord[]>([]);
  const [errors, setErrors] = useState<Errors>({});
  const { isOpen: isOpenEditModal, onOpen: onOpenEditModal, onClose: onCloseEditModal } = useDisclosure();

  const [filterChecklistNo, setFilterChecklistNo] = useState<string>("");
  const [filterContentNameJa, setFilterContentNameJa] = useState<string>("");
  const [filterContentNameEn, setFilterContentNameEn] = useState<string>("");
  const [isSaving, setIsSaving] = useState(false);

  const [considerationsListJa, setConsiderationsListJa] = useState<string[]>([]);
  const [considerationsListEn, setConsiderationsListEn] = useState<string[]>([]);
  const [childItemsList, setChildItemsList] = useState<ChecklistRecord[]>([]);
  const [isReorderMode, setIsReorderMode] = useState(false);
  const [isConsiderationReorderMode, setIsConsiderationReorderMode] = useState(false);

  const filteredMasterData = (() => {
    const principles = (masterData ?? []).filter(m => {
      if (!m || m.nodeClass !== "PRINCIPLE") return false;
      const hasFilterString = (propertyValue: string | null | undefined, filterString: string): boolean => {
        return (propertyValue ?? '').toLowerCase().includes(filterString.toLowerCase());
      };
      const isParentMatched = hasFilterString(m.checklistNo, filterChecklistNo) && hasFilterString(m.contentNameJa, filterContentNameJa) && hasFilterString(m.contentNameEn, filterContentNameEn);
      const isChildMatched = (masterData ?? []).some(c => c && c.nodeClass === "ITEM" && c.parentNodeId === m.nodeId && hasFilterString(c.contentNameJa, filterContentNameJa) && hasFilterString(c.contentNameEn, filterContentNameEn));
      return isParentMatched || isChildMatched;
    }).sort((a, b) => {
      if (a.deleted !== b.deleted) return a.deleted ? 1 : -1;
      const noA = Number(a.checklistNo ?? 0);
      const noB = Number(b.checklistNo ?? 0);
      if (noA !== noB) return noA - noB;
      return (a.order ?? 9999) - (b.order ?? 9999);
    });

    const result: ChecklistRecord[] = [];
    principles.forEach(parent => {
      result.push(parent);

      const hasFilterString = (propertyValue: string | null | undefined, filterString: string): boolean => {
        return (propertyValue ?? '').toLowerCase().includes(filterString.toLowerCase());
      };
      const isParentMatched = hasFilterString(parent.checklistNo, filterChecklistNo) && hasFilterString(parent.contentNameJa, filterContentNameJa) && hasFilterString(parent.contentNameEn, filterContentNameEn);

      const children = (masterData ?? [])
        .filter(m => {
          if (!m || m.nodeClass !== "ITEM" || m.parentNodeId !== parent.nodeId) return false;
          return isParentMatched || (hasFilterString(m.contentNameJa, filterContentNameJa) && hasFilterString(m.contentNameEn, filterContentNameEn));
        })
        .sort((a, b) => {
          if (a.deleted !== b.deleted) return a.deleted ? 1 : -1;
          return (a.order ?? 0) - (b.order ?? 0);
        });
      result.push(...children);
    });
    return result;
  })();

  useEffect(() => {
    fetchMasterData();
  }, []);

  useEffect(() => {
    if (!isOpenEditModal) return;
    const cleanJa = (editTarget.conflictingConsiderationsJa ?? []).map(v => v.replace(/^・/, ""));
    const cleanEn = (editTarget.conflictingConsiderationsEn ?? []).map(v => v.replace(/^・/, ""));
    setConsiderationsListJa(cleanJa);
    setConsiderationsListEn(cleanEn);
    const relatedChildren = masterData.filter(
      m => m.nodeClass === "ITEM" && m.parentNodeId === editTarget.nodeId
    ).sort((a, b) => {
      if (a.deleted !== b.deleted) return a.deleted ? 1 : -1;
      return (a.order ?? 0) - (b.order ?? 0);
    });
    setChildItemsList(JSON.parse(JSON.stringify(relatedChildren)));
    setOriginalChildItemsList(JSON.parse(JSON.stringify(relatedChildren)));
    setIsReorderMode(false);
    setIsConsiderationReorderMode(false);
  }, [isOpenEditModal]);

  const handleOpenEditModal = () => {
    const activePrinciples = masterData.filter(m => m.nodeClass === "PRINCIPLE" && !m.deleted);
    const maxNo = activePrinciples.length > 0
      ? Math.max(...activePrinciples.map(m => parseInt(m.checklistNo || "0")))
      : 0;
    const freshTarget = { ...JSON.parse(JSON.stringify(initialEditTarget)), checklistNo: String(maxNo + 1) };
    setEditTarget(freshTarget);
    setOriginalEditTarget(JSON.parse(JSON.stringify(freshTarget)));
    onOpenEditModal();
  };

  const changeEditTarget = (propertyName: string, value: any) => {
    setEditTarget((prevState) => ({
      ...prevState,
      [propertyName]: value,
    }));
  };

  const fetchMasterData = async () => {
    try {
      const payload = {
        query: `
          query {
            getCheckList(hasDeleted: true) {
              nodeId
              parentNodeId
              nodeClass
              checklistNo
              contentNameJa
              contentNameEn
              conflictingConsiderationsJa
              conflictingConsiderationsEn
              deleted
              order
              createDate
              updateDate
            }
          }`
      };
      const response = await WebAPI.request(payload);
      if (response && response.data && response.data.getCheckList) {
        setMasterData(response.data.getCheckList);
      } else {
        showErrorToast(t('common.message.fetchListFailed'));
      }
    } catch (error) {
      showErrorToast(t('features.MasterMaintenance.CheckListMaster.message.communicationError'));
    }
  };

  const handleEditWithParentFallback = (record: ChecklistRecord) => {
    if (record.nodeClass === "PRINCIPLE") {
      handleEdit(record.nodeId);
    } else {
      const parentRecord = masterData.find(m => m && m.nodeClass === "PRINCIPLE" && m.nodeId === record.parentNodeId);
      if (parentRecord) {
        handleEdit(parentRecord.nodeId);
      } else {
        showErrorToast(t('common.message.fetchInfoFailed'));
      }
    }
  };

  const handleEdit = (nodeId?: string | null) => {
    if (!nodeId) return;
    const selectedRecord = masterData.find(record => record.nodeId === nodeId);
    if (selectedRecord) {
      const target = JSON.parse(JSON.stringify(selectedRecord));
      if (target.nodeClass === "PRINCIPLE" && target.deleted) {
        const activePrinciples = masterData.filter(m => m.nodeClass === "PRINCIPLE" && !m.deleted);
        const maxNo = activePrinciples.length > 0
          ? Math.max(...activePrinciples.map(m => parseInt(m.checklistNo || "0")))
          : 0;
        target.checklistNo = String(maxNo + 1);
      }
      setEditTarget(target);
      setOriginalEditTarget(JSON.parse(JSON.stringify(selectedRecord)));
      onOpenEditModal();
    } else {
      showErrorToast(t('common.message.fetchInfoFailed'));
    }
  };

  const handleSave = async () => {
    if (isSaving) return;
    setIsSaving(true);

    const finalJa = considerationsListJa.filter(v => v.trim() !== "").map(v => `・${v.trim()}`);
    const finalEn = considerationsListEn.filter(v => v.trim() !== "").map(v => `・${v.trim()}`);

    const updatedTarget = {
      ...editTarget,
      conflictingConsiderationsJa: finalJa,
      conflictingConsiderationsEn: finalEn,
    };

    if (!validate(updatedTarget)) {
      toast({
        title: t('common.label.inputError'),
        description: t('common.message.inputErrorContent'),
        status: 'warning',
        duration: 2000,
        isClosable: true,
      });
      setIsSaving(false);
      return;
    }

    const saveRecords: any[] = [];

    const isNewDeletion = !originalEditTarget.deleted && updatedTarget.deleted;
    const isRestoration = Boolean(originalEditTarget.deleted) && !updatedTarget.deleted;

    const principleNodeId = updatedTarget.updateDate ? updatedTarget.nodeId : crypto.randomUUID();

    const isParentChanged =
      updatedTarget.contentNameJa !== originalEditTarget.contentNameJa ||
      updatedTarget.contentNameEn !== originalEditTarget.contentNameEn ||
      JSON.stringify(updatedTarget.conflictingConsiderationsJa) !== JSON.stringify(originalEditTarget.conflictingConsiderationsJa) ||
      JSON.stringify(updatedTarget.conflictingConsiderationsEn) !== JSON.stringify(originalEditTarget.conflictingConsiderationsEn) ||
      updatedTarget.deleted !== originalEditTarget.deleted;

    if (!updatedTarget.updateDate || isParentChanged) {
      saveRecords.push({
        nodeId: principleNodeId,
        parentNodeId: null,
        nodeClass: "PRINCIPLE",
        checklistNo: updatedTarget.checklistNo,
        contentNameJa: updatedTarget.contentNameJa,
        contentNameEn: updatedTarget.contentNameEn,
        conflictingConsiderationsJa: updatedTarget.conflictingConsiderationsJa,
        conflictingConsiderationsEn: updatedTarget.conflictingConsiderationsEn,
        order: updatedTarget.order ?? 0,
        deleted: updatedTarget.deleted,
        updateDate: updatedTarget.updateDate === "EXISTING" ? undefined : updatedTarget.updateDate,
      });
    }

    if (!isNewDeletion) {
      childItemsList.filter(child => child.contentNameJa?.trim()).forEach(child => {
        const original = originalChildItemsList.find(o => o.nodeId === child.nodeId && child.nodeId !== "");

        const isChildChanged = !original ||
          child.contentNameJa !== original.contentNameJa ||
          child.contentNameEn !== original.contentNameEn ||
          child.order !== original.order ||
          child.deleted !== original.deleted;

        if (isChildChanged) {
          saveRecords.push({
            nodeId: child.nodeId ? child.nodeId : crypto.randomUUID(),
            parentNodeId: child.parentNodeId || principleNodeId,
            nodeClass: "ITEM",
            checklistNo: updatedTarget.checklistNo,
            contentNameJa: child.contentNameJa,
            contentNameEn: child.contentNameEn,
            conflictingConsiderationsJa: [],
            conflictingConsiderationsEn: [],
            order: child.order ?? 0,
            deleted: child.deleted,
            updateDate: (original && child.updateDate === "EXISTING") ? undefined : child.updateDate,
          });
        }
      });
    }

    if (isNewDeletion) {
      const itemsToDelete = masterData.filter(
        m => m.nodeClass === "ITEM" && m.parentNodeId === principleNodeId
      );
      for (const item of itemsToDelete) {
        saveRecords.push({
          nodeId: item.nodeId,
          parentNodeId: item.parentNodeId,
          nodeClass: "ITEM",
          checklistNo: item.checklistNo,
          contentNameJa: item.contentNameJa,
          contentNameEn: item.contentNameEn,
          conflictingConsiderationsJa: [],
          conflictingConsiderationsEn: [],
          order: item.order ?? 0,
          deleted: true,
          updateDate: item.updateDate,
        });
      }

      const targetNo = parseInt(updatedTarget.checklistNo || "0");
      const principlesToShift = masterData.filter(
        m => m.nodeClass === "PRINCIPLE" && !m.deleted && parseInt(m.checklistNo || "0") > targetNo
      );
      for (const p of principlesToShift) {
        const newNo = String(parseInt(p.checklistNo) - 1);
        saveRecords.push({
          nodeId: p.nodeId,
          parentNodeId: null,
          nodeClass: "PRINCIPLE",
          checklistNo: newNo,
          contentNameJa: p.contentNameJa,
          contentNameEn: p.contentNameEn,
          conflictingConsiderationsJa: p.conflictingConsiderationsJa,
          conflictingConsiderationsEn: p.conflictingConsiderationsEn,
          order: p.order ?? 0,
          deleted: false,
          updateDate: p.updateDate,
        });
        const items = masterData.filter(m => m.nodeClass === "ITEM" && m.parentNodeId === p.nodeId);
        for (const item of items) {
          saveRecords.push({
            nodeId: item.nodeId,
            parentNodeId: item.parentNodeId,
            nodeClass: "ITEM",
            checklistNo: newNo,
            contentNameJa: item.contentNameJa,
            contentNameEn: item.contentNameEn,
            conflictingConsiderationsJa: [],
            conflictingConsiderationsEn: [],
            order: item.order ?? 0,
            deleted: item.deleted,
            updateDate: item.updateDate,
          });
        }
      }
    } else if (!updatedTarget.updateDate || isRestoration) {
      const insertNo = parseInt(updatedTarget.checklistNo || "0");
      const activePrinciples = masterData.filter(m => m.nodeClass === "PRINCIPLE" && !m.deleted);
      const maxNo = activePrinciples.length > 0
        ? Math.max(...activePrinciples.map(m => parseInt(m.checklistNo || "0")))
        : 0;

      if (insertNo <= maxNo) {
        const principlesToShift = masterData.filter(
          m => m.nodeClass === "PRINCIPLE" && !m.deleted && parseInt(m.checklistNo || "0") >= insertNo
        );
        for (const p of principlesToShift) {
          const newNo = String(parseInt(p.checklistNo) + 1);
          saveRecords.push({
            nodeId: p.nodeId,
            parentNodeId: null,
            nodeClass: "PRINCIPLE",
            checklistNo: newNo,
            contentNameJa: p.contentNameJa,
            contentNameEn: p.contentNameEn,
            conflictingConsiderationsJa: p.conflictingConsiderationsJa,
            conflictingConsiderationsEn: p.conflictingConsiderationsEn,
            order: p.order ?? 0,
            deleted: p.deleted,
            updateDate: p.updateDate,
          });
          const items = masterData.filter(m => m.nodeClass === "ITEM" && m.parentNodeId === p.nodeId);
          for (const item of items) {
            saveRecords.push({
              nodeId: item.nodeId,
              parentNodeId: item.parentNodeId,
              nodeClass: "ITEM",
              checklistNo: newNo,
              contentNameJa: item.contentNameJa,
              contentNameEn: item.contentNameEn,
              conflictingConsiderationsJa: [],
              conflictingConsiderationsEn: [],
              order: item.order ?? 0,
              deleted: item.deleted,
              updateDate: item.updateDate,
            });
          }
        }
      }

      if (isRestoration && originalEditTarget.checklistNo !== updatedTarget.checklistNo) {
        const childrenOfRestored = masterData.filter(
          m => m.nodeClass === "ITEM" && m.parentNodeId === principleNodeId
        );
        for (const child of childrenOfRestored) {
          saveRecords.push({
            nodeId: child.nodeId,
            parentNodeId: child.parentNodeId,
            nodeClass: "ITEM",
            checklistNo: updatedTarget.checklistNo,
            contentNameJa: child.contentNameJa,
            contentNameEn: child.contentNameEn,
            conflictingConsiderationsJa: [],
            conflictingConsiderationsEn: [],
            order: child.order ?? 0,
            deleted: child.deleted,
            updateDate: child.updateDate,
          });
        }
      }
    }

    if (saveRecords.length === 0) {
      setIsSaving(false);
      onCloseEditModal();
      return;
    }

    if (!await putCheckListMaster(saveRecords)) {
      setIsSaving(false);
      return;
    }

    await fetchMasterData();
    setIsSaving(false);
    onCloseEditModal();
  };

  const validate = (target: ChecklistRecord) => {
    if (target.deleted) return true;

    setErrors({});
    const localErrors: Errors = {};
    const required = (value?: string | null) => Boolean(value || null);

    const checkRequired = (prop: string, label: string) => {
      if (!required(target[prop as keyof ChecklistRecord] as string)) {
        localErrors[prop] = {
          isError: true,
          errorMessage: t('common.message.requiredField', { fieldName: label }),
        };
      }
    };

    checkRequired("contentNameJa", t('features.MasterMaintenance.CheckListMaster.field.principlesNameJa'));
    checkRequired("contentNameEn", t('features.MasterMaintenance.CheckListMaster.field.principlesNameEn'));

    setErrors(localErrors);
    return Object.keys(localErrors).length === 0;
  };

  const putCheckListMaster = async (records: any[]) => {
    try {
      const result = await WebAPI.request({
        query: `
          mutation putCheckListMaster($input: [CheckListMasterInput]!) {
            putCheckListMaster(input: $input) { status }
          }
        `,
        variables: { input: records }
      });

      if (result?.data?.putCheckListMaster?.status === "success") {
        toast({
          title: TITLE,
          description: t('common.message.inputSaved'),
          status: 'success',
          duration: 5000,
          isClosable: true,
        });
        return true;
      } else if (result?.data?.putCheckListMaster?.status === 'duplicate') {
        toast({ title: TITLE, description: t('features.MasterMaintenance.CheckListMaster.message.duplicateId'), status: 'warning', duration: 5000, isClosable: true });
        return false;
      } else if (result?.data?.putCheckListMaster?.status === 'expired') {
        toast({ title: TITLE, description: Messages.expiredMessage, status: 'warning', duration: 5000, isClosable: true });
        return false;
      } else {
        showErrorToast(t('common.message.saveFailed'));
        return false;
      }
    } catch (error) {
      showErrorToast(t('common.message.saveFailed'));
      return false;
    }
  };

  const showErrorToast = (msg: string) => {
    toast({ title: TITLE, description: msg, status: 'error', duration: 5000, isClosable: true });
  };

  const handleClose = async () => {
    onCloseEditModal();
  };

  const handleCloseComplete = () => {
    setEditTarget(initialEditTarget);
    setOriginalEditTarget(initialEditTarget);
    setOriginalChildItemsList([]);
    setConsiderationsListJa([]);
    setConsiderationsListEn([]);
    setChildItemsList([]);
    setErrors({});
  };

  const addConsiderationRow = () => {
    setConsiderationsListJa([...considerationsListJa, ""]);
    setConsiderationsListEn([...considerationsListEn, ""]);
  };

  const removeConsiderationRow = (index: number) => {
    setConsiderationsListJa(considerationsListJa.filter((_, i) => i !== index));
    setConsiderationsListEn(considerationsListEn.filter((_, i) => i !== index));
  };

  const updateConsiderationValue = (lang: 'ja' | 'en', index: number, value: string) => {
    if (lang === 'ja') {
      const next = [...considerationsListJa]; next[index] = value; setConsiderationsListJa(next);
    } else {
      const next = [...considerationsListEn]; next[index] = value; setConsiderationsListEn(next);
    }
  };

  const moveConsiderationItem = (index: number, direction: 'up' | 'down') => {
    if (direction === 'up' && index === 0) return;
    if (direction === 'down' && index === considerationsListJa.length - 1) return;

    const targetIndex = direction === 'up' ? index - 1 : index + 1;

    const nextJa = [...considerationsListJa];
    const tempJa = nextJa[index];
    nextJa[index] = nextJa[targetIndex];
    nextJa[targetIndex] = tempJa;
    setConsiderationsListJa(nextJa);

    const nextEn = [...considerationsListEn];
    const tempEn = nextEn[index];
    nextEn[index] = nextEn[targetIndex];
    nextEn[targetIndex] = tempEn;
    setConsiderationsListEn(nextEn);
  };

  const addChildItemRow = () => {
    const activeChildren = childItemsList.filter(c => !c.deleted);
    setChildItemsList([...childItemsList, {
      nodeId: "",
      parentNodeId: editTarget.nodeId,
      nodeClass: "ITEM",
      checklistNo: editTarget.checklistNo,
      contentNameJa: "",
      contentNameEn: "",
      conflictingConsiderationsJa: [],
      conflictingConsiderationsEn: [],
      order: activeChildren.length + 1,
      deleted: false,
      createDate: "",
      updateDate: "",
    }]);
  };

  const handleToggleChildDelete = (index: number, isDeleted: boolean) => {
    const next = [...childItemsList];
    next[index] = { ...next[index], deleted: isDeleted };

    if (isDeleted) {
      next[index].order = 999;
    }

    let activeCount = 0;
    const reordered = next.map(item => {
      if (!item.deleted) {
        activeCount += 1;
        return { ...item, order: activeCount };
      }
      return item;
    });

    setChildItemsList(reordered);
  };

  const updateChildItemValue = (index: number, field: keyof ChecklistRecord, value: any) => {
    const next = [...childItemsList];
    next[index] = { ...next[index], [field]: value };
    setChildItemsList(next);
  };

  const moveChildItem = (index: number, direction: 'up' | 'down') => {
    const activeItems = childItemsList.filter(c => !c.deleted);
    const targetItem = childItemsList[index];
    const activeIndex = activeItems.findIndex(c => c.nodeId === targetItem.nodeId && c.contentNameJa === targetItem.contentNameJa);

    if (direction === 'up' && activeIndex === 0) return;
    if (direction === 'down' && activeIndex === activeItems.length - 1) return;

    const targetActiveIndex = direction === 'up' ? activeIndex - 1 : activeIndex + 1;
    const swapTargetItem = activeItems[targetActiveIndex];

    const globalSwapTargetIndex = childItemsList.findIndex(c => c.nodeId === swapTargetItem.nodeId && c.contentNameJa === swapTargetItem.contentNameJa);

    const next = [...childItemsList];
    const temp = next[index];
    next[index] = next[globalSwapTargetIndex];
    next[globalSwapTargetIndex] = temp;

    let activeCount = 0;
    const reordered = next.map(item => {
      if (!item.deleted) {
        activeCount += 1;
        return { ...item, order: activeCount };
      }
      return item;
    });

    setChildItemsList(reordered);
  };

  const toggleReorderMode = () => {
    setIsReorderMode(prev => !prev);
  };

  const toggleConsiderationReorderMode = () => {
    setIsConsiderationReorderMode(prev => !prev);
  };

  const getChecklistNoOptions = () => {
    const activePrinciples = masterData.filter(m => m.nodeClass === "PRINCIPLE" && !m.deleted);
    const maxNo = activePrinciples.length > 0
      ? Math.max(...activePrinciples.map(m => parseInt(m.checklistNo || "0")))
      : 0;
    return Array.from({ length: maxNo + 1 }, (_, i) => i + 1);
  };

  return (
    <>
      <Box h="100%" mt="16px" px="24px">
        <Box mb="16px">
          <Button colorScheme='blue' onClick={handleOpenEditModal}>
            {t('common.button.newRegistration')}
          </Button>
        </Box>

        <TableContainer overflowX="auto" overflowY="auto" w="100%" h="calc(100dvh - 200px)" border="1px" borderColor="gray.200" borderRadius="md">
          <Table variant="simple" style={{ tableLayout: "fixed", borderCollapse: "separate" }} cellSpacing={0} cellPadding={0} className="mm-ceas-table">
            <colgroup>
              <col width={150} />
              <col width={450} />
              <col width={450} />
              <col width={330} />
              <col width={330} />
              <col width={100} />
              <col width={120} />
              <col width={180} />
              <col width={180} />
            </colgroup>
            <Thead position="sticky" top={0} zIndex={2} boxShadow={`0 -1px 0 #EEEEEE`}>
              <Tr h={50}>
                <Th {...headerCellStyle} {...fixedCol1Style}>
                  {t('features.MasterMaintenance.CheckListMaster.field.no')}
                  <FilterTextBox placeholder={t('features.MasterMaintenance.CheckListMaster.placeholder.search')} filterValue={filterChecklistNo} setFilterValue={setFilterChecklistNo} />
                </Th>
                <Th {...headerCellStyle} {...fixedCol2Style}>
                  {t('features.MasterMaintenance.CheckListMaster.field.principlesJa')}
                  <FilterTextBox placeholder={t('features.MasterMaintenance.CheckListMaster.placeholder.search')} filterValue={filterContentNameJa} setFilterValue={setFilterContentNameJa} />
                </Th>
                <Th {...headerCellStyle}>
                  {t('features.MasterMaintenance.CheckListMaster.field.principlesEn')}
                  <FilterTextBox placeholder={t('features.MasterMaintenance.CheckListMaster.placeholder.search')} filterValue={filterContentNameEn} setFilterValue={setFilterContentNameEn} />
                </Th>
                <Th {...headerCellStyle}>{t('features.MasterMaintenance.CheckListMaster.field.conflictingConsiderationsJa')}</Th>
                <Th {...headerCellStyle}>{t('features.MasterMaintenance.CheckListMaster.field.conflictingConsiderationsEn')}</Th>
                <Th {...headerCellStyle}>{t('common.label.sortOrder')}</Th>
                <Th {...headerCellStyle}>{t('common.label.deleteFlag')}</Th>
                <Th {...headerCellStyle}>{t('common.label.createDate')}</Th>
                <Th {...headerCellStyle}>{t('common.label.updateDate')}</Th>
              </Tr>
            </Thead>
            <Tbody>
              {(() => {
                return filteredMasterData.map(record => {
                  const isPrinciple = record.nodeClass === "PRINCIPLE";
                  const bgColor = record.deleted ? "gray.100" : "white";

                  const childIndexDisplay = isPrinciple
                    ? ""
                    : `└ ${record.checklistNo}-${record.order}`;

                  return (
                    <Tr role="group" key={record.nodeId} _hover={{ bg: "gray.50" }}>
                      <Td {...bodyCellStyle} _groupHover={hoverStyle} bgColor={bgColor} {...fixedCol1Style} textAlign="center" pl={isPrinciple ? 4 : 12}>
                        <Button
                          variant="link"
                          textDecoration="underline"
                          color="blue.500"
                          fontWeight="bold"
                          onClick={(e) => { e.currentTarget.blur(); handleEditWithParentFallback(record); }}
                        >
                          {isPrinciple ? record.checklistNo : childIndexDisplay}
                        </Button>
                      </Td>

                      <Td {...bodyCellStyle} _groupHover={hoverStyle} bgColor={bgColor} {...fixedCol2Style} pl={isPrinciple ? 4 : 8} whiteSpace="pre-wrap">
                        {!isPrinciple && "└ "}
                        {record.contentNameJa}
                      </Td>

                      <Td {...bodyCellStyle} _groupHover={hoverStyle} bgColor={bgColor} pl={isPrinciple ? 4 : 8} color={isPrinciple ? "black" : "gray.800"} whiteSpace="pre-wrap">
                        {!isPrinciple && "└ "}
                        {record.contentNameEn}
                      </Td>

                      <Td {...bodyCellStyle} _groupHover={hoverStyle} bgColor={bgColor} color="gray.500" fontSize="xs" p={4} whiteSpace="pre-wrap">
                        {isPrinciple ? (
                          record.conflictingConsiderationsJa && record.conflictingConsiderationsJa.length > 0 ? (
                            <UnorderedList spacing={1} styleType="none" ml={0}>
                              {record.conflictingConsiderationsJa.map((item, i) => (
                                <ListItem key={i}>{item}</ListItem>
                              ))}
                            </UnorderedList>
                          ) : t('features.MasterMaintenance.CheckListMaster.placeholder.none')
                        ) : ""}
                      </Td>

                      <Td {...bodyCellStyle} _groupHover={hoverStyle} bgColor={bgColor} color="gray.500" fontSize="xs" p={4} whiteSpace="pre-wrap">
                        {isPrinciple ? (
                          record.conflictingConsiderationsEn && record.conflictingConsiderationsEn.length > 0 ? (
                            <UnorderedList spacing={1} styleType="none" ml={0}>
                              {record.conflictingConsiderationsEn.map((item, i) => (
                                <ListItem key={i}>{item}</ListItem>
                              ))}
                            </UnorderedList>
                          ) : t('features.MasterMaintenance.CheckListMaster.placeholder.none')
                        ) : ""}
                      </Td>

                      <Td {...bodyCellStyle} _groupHover={hoverStyle} bgColor={bgColor} textAlign="right" pr={4}>{record.order}</Td>

                      <Td {...bodyCellStyle} _groupHover={hoverStyle} bgColor={bgColor} textAlign="center">
                        <Tag size="md" colorScheme={record.deleted ? 'red' : 'green'}>{record.deleted ? 'true' : 'false'}</Tag>
                      </Td>

                      <Td {...bodyCellStyle} _groupHover={hoverStyle} bgColor={bgColor} fontSize="xs">{formatLocalDateTime(record.createDate)}</Td>

                      <Td {...bodyCellStyle} _groupHover={hoverStyle} bgColor={bgColor} fontSize="xs">{formatLocalDateTime(record.updateDate)}</Td>
                    </Tr>
                  );
                });
              })()}
            </Tbody>
          </Table>
        </TableContainer>
      </Box>

      <Modal isOpen={isOpenEditModal} onClose={onCloseEditModal} closeOnOverlayClick={false} onCloseComplete={handleCloseComplete} scrollBehavior="inside" size="6xl" isCentered>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            <Text>
              {editTarget.updateDate
                ? t('features.MasterMaintenance.CheckListMaster.modal.editTitle')
                : t('features.MasterMaintenance.CheckListMaster.modal.newTitle')}
            </Text>
          </ModalHeader>
          <ModalBody>
            <VStack spacing={6} align="stretch">

              <FormControl>
                <Flex align="center">
                  <FormLabel style={labelStyle} mb={0}>
                    {t('features.MasterMaintenance.CheckListMaster.field.no')}
                  </FormLabel>
                  {(editTarget.updateDate && !originalEditTarget.deleted) ? (
                    <Text mt={1} fontSize="md" fontWeight="bold">{editTarget.checklistNo}</Text>
                  ) : (
                    <Select
                      value={editTarget.checklistNo}
                      onChange={(e) => changeEditTarget("checklistNo", e.target.value)}
                      w={300}
                    >
                      {getChecklistNoOptions().map(no => {
                        const activePrinciples = masterData.filter(m => m.nodeClass === "PRINCIPLE" && !m.deleted);
                        const maxNo = activePrinciples.length > 0
                          ? Math.max(...activePrinciples.map(m => parseInt(m.checklistNo || "0")))
                          : 0;
                        return (
                          <option key={no} value={String(no)}>
                            {no <= maxNo
                              ? t('features.MasterMaintenance.CheckListMaster.option.insertAt', { no })
                              : t('features.MasterMaintenance.CheckListMaster.option.appendToEnd', { no })}
                          </option>
                        );
                      })}
                    </Select>
                  )}
                </Flex>
              </FormControl>

              <Box
                opacity={editTarget.deleted ? 0.45 : 1}
                pointerEvents={editTarget.deleted ? "none" : "auto"}
                transition="opacity 0.15s"
              >
                <VStack spacing={6} align="stretch">
                  <FormControl isInvalid={errors.contentNameJa?.isError || errors.contentNameEn?.isError}>
                    <VStack align="stretch" spacing={2}>
                      <FormLabel style={labelStyle} mb={0}>{t('features.MasterMaintenance.CheckListMaster.field.principles')}</FormLabel>
                      <Grid templateColumns="1fr 1fr 160px" gap={6}>
                        <GridItem>
                          <Text fontSize="xs" color="gray.500" mb={1}>[ {t('features.MasterMaintenance.CheckListMaster.japanese')} ]</Text>
                          <Textarea
                            value={editTarget.contentNameJa || ""}
                            onChange={(e) => changeEditTarget("contentNameJa", e.target.value)}
                            placeholder={t('features.MasterMaintenance.CheckListMaster.placeholder.contentNameJa')}
                            maxLength={150}
                            size="md"
                            rows={3}
                            resize="vertical"
                          />
                          <FormErrorMessage>{errors.contentNameJa?.errorMessage}</FormErrorMessage>
                        </GridItem>
                        <GridItem>
                          <Text fontSize="xs" color="gray.500" mb={1}>[ {t('features.MasterMaintenance.CheckListMaster.english')} ]</Text>
                          <Textarea
                            value={editTarget.contentNameEn || ""}
                            onChange={(e) => changeEditTarget("contentNameEn", e.target.value)}
                            placeholder={t('features.MasterMaintenance.CheckListMaster.placeholder.contentNameEn')}
                            maxLength={150}
                            size="md"
                            rows={3}
                            resize="vertical"
                          />
                          <FormErrorMessage>{errors.contentNameEn?.errorMessage}</FormErrorMessage>
                        </GridItem>
                        <GridItem></GridItem>
                      </Grid>
                    </VStack>
                  </FormControl>

                  <Divider />

                  <FormControl>
                    <VStack align="stretch" spacing={2}>
                      <FormLabel style={labelStyle} mb={0}>
                        {t('features.MasterMaintenance.CheckListMaster.field.conflictingConsiderations')}
                      </FormLabel>
                      <Grid templateColumns="1fr 1fr 160px" gap={6}>
                        <GridItem pl={isConsiderationReorderMode ? "42px" : "0px"}>
                          <Text fontSize="xs" color="gray.500" mb={1}>[ {t('features.MasterMaintenance.CheckListMaster.japanese')} ]</Text>
                        </GridItem>
                        <GridItem>
                          <Text fontSize="xs" color="gray.500" mb={1}>[ {t('features.MasterMaintenance.CheckListMaster.english')} ]</Text>
                        </GridItem>
                        <GridItem></GridItem>
                      </Grid>
                      <VStack align="stretch" w="100%" spacing={3}>
                        {considerationsListJa.map((_, idx) => (
                          <Grid templateColumns="1fr 1fr 160px" gap={6} key={idx} alignItems="center">
                            <GridItem>
                              <HStack spacing={2}>
                                {isConsiderationReorderMode && (
                                  <VStack spacing={1}>
                                    <IconButton
                                      aria-label="Move Up"
                                      icon={<ArrowUpIcon />}
                                      size="xs"
                                      variant="outline"
                                      isDisabled={idx === 0}
                                      onClick={() => moveConsiderationItem(idx, 'up')}
                                    />
                                    <IconButton
                                      aria-label="Move Down"
                                      icon={<ArrowDownIcon />}
                                      size="xs"
                                      variant="outline"
                                      isDisabled={idx === considerationsListJa.length - 1}
                                      onClick={() => moveConsiderationItem(idx, 'down')}
                                    />
                                  </VStack>
                                )}
                                <Textarea size="md" value={considerationsListJa[idx] || ""} onChange={(e) => updateConsiderationValue('ja', idx, e.target.value)} placeholder={`${t('features.MasterMaintenance.CheckListMaster.placeholder.contentNameJa')}`} maxLength={150} rows={2} resize="vertical" />
                              </HStack>
                            </GridItem>
                            <GridItem>
                              <Textarea size="md" value={considerationsListEn[idx] || ""} onChange={(e) => updateConsiderationValue('en', idx, e.target.value)} placeholder={`${t('features.MasterMaintenance.CheckListMaster.placeholder.contentNameEn')}`} maxLength={150} rows={2} resize="vertical" />
                            </GridItem>
                            <GridItem>
                              {!isConsiderationReorderMode && (
                                <Button
                                  colorScheme="red"
                                  onClick={() => removeConsiderationRow(idx)}
                                  leftIcon={<FaCircleMinus />}
                                  size="md"
                                >
                                  {t('features.MasterMaintenance.CheckListMaster.button.delete')}
                                </Button>
                              )}
                            </GridItem>
                          </Grid>
                        ))}
                        {considerationsListJa.length === 0 && <Text fontSize="xs" color="gray.400" mt={2}>{t('features.MasterMaintenance.CheckListMaster.placeholder.noData')}</Text>}
                      </VStack>
                      <HStack spacing={4}>
                        <Button
                          colorScheme="teal"
                          onClick={addConsiderationRow}
                          leftIcon={<FaPlusCircle />}
                          size="md"
                        >
                          {t('common.button.add')}
                        </Button>
                        <Button
                          colorScheme={isConsiderationReorderMode ? "orange" : "gray"}
                          onClick={toggleConsiderationReorderMode}
                          isDisabled={considerationsListJa.length <= 1}
                          size="md"
                        >
                          {isConsiderationReorderMode
                            ? t('features.MasterMaintenance.CheckListMaster.button.reorderComplete')
                            : t('features.MasterMaintenance.CheckListMaster.button.reorder')}
                        </Button>
                      </HStack>
                    </VStack>
                  </FormControl>

                  <Divider />

                  <FormControl>
                    <VStack align="stretch" spacing={2}>
                      <FormLabel style={labelStyle} mb={0}>
                        {t('features.MasterMaintenance.CheckListMaster.field.items')}
                      </FormLabel>
                      <Grid templateColumns="1fr 1fr 160px" gap={6}>
                        <GridItem pl={isReorderMode ? "42px" : "0px"}>
                          <Text fontSize="xs" color="gray.500" mb={1}>[ {t('features.MasterMaintenance.CheckListMaster.japanese')} ]</Text>
                        </GridItem>
                        <GridItem>
                          <Text fontSize="xs" color="gray.500" mb={1}>[ {t('features.MasterMaintenance.CheckListMaster.english')} ]</Text>
                        </GridItem>
                        <GridItem>
                          <Text fontSize="xs" color="gray.500" mb={1}>[ {t('common.label.deleteFlag')} ]</Text>
                        </GridItem>
                      </Grid>
                      <VStack align="stretch" w="100%" spacing={3}>
                        {(() => {
                          const nonDeletedChildren = childItemsList.filter(c => !c.deleted);
                          return childItemsList.map((child, idx) => {
                            if (isReorderMode && child.deleted) return null;
                            const filteredIdx = nonDeletedChildren.findIndex(c => c.nodeId === child.nodeId && c.contentNameJa === child.contentNameJa);

                            return (
                              <Grid
                                templateColumns="1fr 1fr 160px"
                                gap={6}
                                key={idx}
                                alignItems="center"
                                p={isReorderMode ? 2 : 0}
                                border={isReorderMode ? "1px" : "0px"}
                                borderColor="gray.100"
                                borderRadius="md"
                                opacity={child.deleted ? 0.5 : 1}
                                bgColor={child.deleted ? "gray.50" : "transparent"}
                              >
                                <GridItem colSpan={1}>
                                  <HStack spacing={2}>
                                    {isReorderMode && (
                                      <VStack spacing={1}>
                                        <IconButton
                                          aria-label="Move Up"
                                          icon={<ArrowUpIcon />}
                                          size="xs"
                                          variant="outline"
                                          isDisabled={filteredIdx === 0}
                                          onClick={() => moveChildItem(idx, 'up')}
                                        />
                                        <IconButton
                                          aria-label="Move Down"
                                          icon={<ArrowDownIcon />}
                                          size="xs"
                                          variant="outline"
                                          isDisabled={filteredIdx === nonDeletedChildren.length - 1}
                                          onClick={() => moveChildItem(idx, 'down')}
                                        />
                                      </VStack>
                                    )}
                                    <Textarea size="md" value={child.contentNameJa} onChange={(e) => updateChildItemValue(idx, 'contentNameJa', e.target.value)} placeholder={t('features.MasterMaintenance.CheckListMaster.placeholder.contentNameJa')} isDisabled={child.deleted} rows={2} resize="vertical" />
                                  </HStack>
                                </GridItem>
                                <GridItem colSpan={1}>
                                  <Textarea size="md" value={child.contentNameEn} onChange={(e) => updateChildItemValue(idx, 'contentNameEn', e.target.value)} placeholder={t('features.MasterMaintenance.CheckListMaster.placeholder.contentNameEn')} isDisabled={child.deleted} rows={2} resize="vertical" />
                                </GridItem>
                                <GridItem colSpan={1}>
                                  {!isReorderMode ? (
                                    <HStack spacing={2}>
                                      <Button variant="ghost" size="sm" px={1} fontSize="xs" fontWeight="bold" _hover={{ bg: 'transparent' }} color={!child.deleted ? "gray.700" : "gray.300"} onClick={() => handleToggleChildDelete(idx, false)}>
                                        {t('common.label.notDeleted')}
                                      </Button>
                                      <Switch
                                        isChecked={child.deleted}
                                        onChange={(e) => handleToggleChildDelete(idx, e.target.checked)}
                                      />
                                      <Button variant="ghost" size="sm" px={1} fontSize="xs" fontWeight="bold" _hover={{ bg: 'transparent' }} color={child.deleted ? "blue.600" : "gray.300"} onClick={() => handleToggleChildDelete(idx, true)}>
                                        {t('common.label.deleted')}
                                      </Button>
                                    </HStack>
                                  ) : null}
                                </GridItem>
                              </Grid>
                            );
                          });
                        })()}
                        {childItemsList.length === 0 && <Text fontSize="xs" color="gray.400" mt={2}>{t('features.MasterMaintenance.CheckListMaster.placeholder.noData')}</Text>}
                      </VStack>
                      <HStack spacing={4}>
                        <Button
                          colorScheme="teal"
                          onClick={addChildItemRow}
                          leftIcon={<FaPlusCircle />}
                          size="md"
                        >
                          {t('common.button.add')}
                        </Button>
                        <Button
                          colorScheme={isReorderMode ? "orange" : "gray"}
                          onClick={toggleReorderMode}
                          isDisabled={childItemsList.filter(c => !c.deleted).length <= 1}
                          size="md"
                        >
                          {isReorderMode
                            ? t('features.InvestmentScreening.Scheduling.button.reorderComplete')
                            : t('features.MasterMaintenance.CheckListMaster.button.reorder')}
                        </Button>
                      </HStack>
                    </VStack>
                  </FormControl>

                </VStack>
              </Box>

              {editTarget.updateDate && (
                <>
                  <Divider />
                  <FormControl h={70}>
                    <Flex align="start">
                      <FormLabel style={labelStyle}>
                        {t('common.label.deleteFlag')}
                      </FormLabel>
                      <VStack align="left">
                        <HStack>
                          <Button fontWeight="bold" userSelect="none" cursor="pointer" variant="ghost" _hover={{ bg: 'transparent' }}
                            onClick={() => {
                              changeEditTarget("deleted", false);
                              setChildItemsList(prev => prev.map(child => ({ ...child, deleted: child.order === 999 ? true : false })));
                            }}>{t('common.label.notDeleted')}</Button>
                          <Switch
                            isChecked={editTarget.deleted}
                            onChange={(e) => {
                              changeEditTarget("deleted", e.target.checked);
                              if (!e.target.checked) {
                                setChildItemsList(prev => prev.map(child => ({ ...child, deleted: child.order === 999 ? true : false })));
                              }
                            }}
                          />
                          <Button fontWeight="bold" userSelect="none" cursor="pointer" variant="ghost" _hover={{ bg: 'transparent' }}
                            onClick={() => {
                              changeEditTarget("deleted", true);
                            }}>{t('common.label.deleted')}</Button>
                        </HStack>
                      </VStack>
                    </Flex>
                  </FormControl>
                </>
              )}

              {editTarget.updateDate && (
                <>
                  <Divider />
                  <FormControl><Flex align="start"><FormLabel style={labelStyle}>{t('common.label.createDate')}</FormLabel><Text mt={1} fontSize="md">{formatLocalDateTime(editTarget.createDate)}</Text></Flex></FormControl>
                  <FormControl><Flex align="start"><FormLabel style={labelStyle}>{t('common.label.updateDate')}</FormLabel><Text mt={1} fontSize="md">{formatLocalDateTime(editTarget.updateDate)}</Text></Flex></FormControl>
                </>
              )}

            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button colorScheme="blue" isDisabled={isSaving} isLoading={isSaving} onClick={handleSave} m={1}>
              {t('common.button.save')}
            </Button>
            <Button onClick={handleClose} m={1}>
              {t('common.button.close')}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
};

export default CheckListMaster;